import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { PaginateProps } from "./paginate";
import { PageInfo } from "~/operations";

export const AFTER_PARAM_KEY = "after";
export const FIRST_PARAM_KEY = "first";
export const BEFORE_PARAM_KEY = "before";
export const LAST_PARAM_KEY = "last";
export const PAGE_SIZE_PARAM_KEY = "pageSize";
export const DEFAULT_PAGE_SIZES = [10, 25, 50, 100] as const;

const getParamKey = (keys: string[]) => keys.flatMap((k) => k || []).join(".");

const getPageSizeParamKey = (pageKey = "") =>
  getParamKey([pageKey, PAGE_SIZE_PARAM_KEY]);

const getBeforeParamKey = (pageKey = "") =>
  getParamKey([pageKey, BEFORE_PARAM_KEY]);

const getAfterParamKey = (pageKey = "") =>
  getParamKey([pageKey, AFTER_PARAM_KEY]);

export type UsePaginateProps = {
  prefix?: string;
  pageSizes?: readonly number[];
  defaultPageSize?: number;
};

export function usePaginate({
  prefix = "",
  pageSizes = DEFAULT_PAGE_SIZES,
  defaultPageSize = pageSizes[0],
}: UsePaginateProps | undefined = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSizeParamKey = getPageSizeParamKey(prefix);
  const afterParamKey = getAfterParamKey(prefix);
  const beforeParamKey = getBeforeParamKey(prefix);

  const pageSizeParam = searchParams.get(pageSizeParamKey);
  const beforeParam = searchParams.get(beforeParamKey);
  const afterParam = searchParams.get(afterParamKey);

  const pageSize = pageSizeParam ? parseInt(pageSizeParam) : defaultPageSize;
  const before = beforeParam ? beforeParam : undefined;
  const after = afterParam ? afterParam : undefined;

  const beforeVars = before ? { before, last: pageSize } : undefined;
  const afterVars = after ? { after, first: pageSize } : undefined;
  const pageVars = beforeVars
    ? beforeVars
    : afterVars
      ? afterVars
      : { first: pageSize };

  //
  const pageProps = useCallback(
    (pageInfo?: Partial<PageInfo>): PaginateProps => {
      const { hasPreviousPage, hasNextPage, startCursor, endCursor } =
        pageInfo || {};

      const prevCursor = hasPreviousPage ? startCursor : undefined;
      const nextCursor = hasNextPage ? endCursor : undefined;

      const prevUrl = prevCursor ? new URL(location.href) : undefined;
      const nextUrl = nextCursor ? new URL(location.href) : undefined;

      if (prevUrl && prevCursor) {
        prevUrl.searchParams.set(beforeParamKey, prevCursor);
        prevUrl.searchParams.delete(afterParamKey);
      }

      if (nextUrl && nextCursor) {
        nextUrl.searchParams.set(afterParamKey, nextCursor);
        nextUrl.searchParams.delete(beforeParamKey);
      }

      const pageSizeItems = pageSizes.map((pageSize) => {
        const url = new URL(location.href);
        url.searchParams.set(pageSizeParamKey, pageSize.toString());
        url.searchParams.delete(beforeParamKey);
        url.searchParams.delete(afterParamKey);
        return { pageSize, url };
      });

      return {
        prevUrl,
        nextUrl,
        pageSizeItems,
        pageSize,
      };
    },
    [
      location.href,
      pageSizeParamKey,
      beforeParamKey,
      afterParamKey,
      pageSize,
      pageSizes,
    ],
  );

  const cursorUrl = useCallback(
    (
      href: string | URL,
      cursor?: { beforeCursor?: string; afterCursor?: string },
    ) => {
      const url = new URL(href);
      const { beforeCursor, afterCursor } = cursor || {};

      if (beforeCursor) {
        url.searchParams.set(beforeParamKey, beforeCursor);
        url.searchParams.delete(afterParamKey);
        return url;
      }

      if (afterCursor) {
        url.searchParams.set(afterParamKey, afterCursor);
        url.searchParams.delete(beforeParamKey);
        return url;
      }

      return url;
    },
    [beforeParamKey, afterParamKey],
  );

  const resetCursorUrl = useCallback(
    (href: string | URL) => {
      const url = new URL(href);
      url.searchParams.delete(beforeParamKey);
      url.searchParams.delete(afterParamKey);
      return url;
    },
    [beforeParamKey, afterParamKey],
  );

  const resetPageSizeUrl = useCallback(
    (href: string | URL) => {
      const url = new URL(href);
      url.searchParams.delete(pageSizeParamKey);
      return url;
    },
    [pageSizeParamKey],
  );

  const resetUrl = useCallback(
    (href: string | URL) => {
      return resetPageSizeUrl(resetCursorUrl(href));
    },
    [resetCursorUrl, resetPageSizeUrl],
  );

  const resetCursor = useCallback(() => {
    const url = resetCursorUrl(location.href);
    setSearchParams(url.searchParams);
  }, [location.href, resetCursorUrl]);

  const reset = useCallback(() => {
    const url = resetUrl(location.href);
    setSearchParams(url.searchParams);
  }, [location.href, resetUrl]);

  return {
    pageSizeParam,
    pageSize,
    afterParam,
    after,
    beforeParam,
    before,
    pageVars,
    pageProps,
    reset,
    resetUrl,
    resetCursor,
    resetCursorUrl,
    cursorUrl,
  };
}
